import { Form, Input } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Modal from '../../../../shared/components/modal/modal';
import { handleServerFormErrors } from '../../../../shared/utils/error-handling/error-handling';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useEditUserEmailMutation, useGetUserQuery } from '../../api/user-api';
import { currentUserAlertId, currentUserEmailFormModalId } from '../../constants/constants';
import { User } from '../../models/user';

const CurrentUserEmailEditModal = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['current-user', 'common']);
  const dispatch = useDispatch();

  const [form] = Form.useForm<Pick<User, 'email'>>();

  const [editCurrentUserEmail, { isLoading: isSubmitting }] = useEditUserEmailMutation();

  const { data: currentUserData } = useGetUserQuery();

  const closeUserEmailEditModal = () => {
    dispatch(closeModal(currentUserEmailFormModalId));
  };

  const submitForm = (formData: Pick<User, 'email'>) => {
    editCurrentUserEmail(formData)
      .unwrap()
      .then(() => {
        closeUserEmailEditModal();
        dispatch(
          setAlert({
            id: currentUserAlertId,
            type: 'info',
            title: t('editUserInfo.message.emailEditSuccess'),
          }),
        );
      })
      .catch(error => {
        handleServerFormErrors(form, error.data?.errors);
      });
  };

  const initialValues: Pick<User, 'email'> = useMemo(() => {
    return {
      email: currentUserData?.email || '',
    };
  }, [currentUserData]);

  useEffect(() => {
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Modal
      title={t('editUserInfo.label.editUsersEmail')}
      onCancel={() => !isSubmitting && closeUserEmailEditModal()}
      isLoading={isSubmitting}
      onOk={() => form.submit()}
      isOpen={isOpen}
      afterClose={afterClose}
      formId={currentUserEmailFormModalId}
    >
      <Form
        id={currentUserEmailFormModalId}
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={submitForm}
      >
        <Form.Item
          name="email"
          label={t('label.email', { ns: 'common' })}
          rules={[
            { required: true, message: t('errors.emailRequired', { ns: 'common' }) },
            {
              type: 'email',
              validateTrigger: 'blur',
              message: t('errors.emailFormat', { ns: 'common' }),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('email') !== initialValues.email) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('editUserInfo.validationError.currentEmailError')));
              },
            }),
          ]}
          normalize={value => value.trim()}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const CurrentUserEmailFormModal = (): JSX.Element => {
  return <LazyModal modalId={currentUserEmailFormModalId} Component={CurrentUserEmailEditModal} />;
};
export default CurrentUserEmailFormModal;
